import axios from 'axios';
import { ChatThread, ChatMessage, ChatAttachment, ChatAvailability, ChatCorrespondent } from '../types/chat';
import { API_URL } from '../config/apiConfig';
import { getStoredHotelId } from './authService';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const chatService = {
  // SCRUM-15: structured hotel messaging correspondents
  listCorrespondents: async (): Promise<ChatCorrespondent[]> => {
    const response = await api.get('/api/v1/chat/correspondents');
    const data = response.data;
    if (Array.isArray(data?.correspondents)) return data.correspondents;
    if (Array.isArray(data)) return data;
    return [];
  },

  // Create a new chat thread
  createThread: async (checkinId?: string, recipientType?: string): Promise<ChatThread> => {
    const body: Record<string, string> = {};
    if (checkinId) body.checkin_id = checkinId;
    if (recipientType) body.recipient_type = recipientType;
    // Always send hotel_id: hotel principal JWTs often omit the claim; portal stores it at login.
    const hotelId = getStoredHotelId();
    if (hotelId) body.hotel_id = hotelId;
    const response = await api.post('/api/v1/chat/threads', body);
    return response.data;
  },

  // Get threads for hotel
  getThreads: async (filters?: { status?: string; search?: string }): Promise<ChatThread[]> => {
    const response = await api.get('/api/v1/chat/threads', { params: filters });
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.threads)) return data.threads;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  },

  // Get thread details
  getThread: async (threadId: string): Promise<ChatThread> => {
    const response = await api.get(`/api/v1/chat/threads/${threadId}`);
    return response.data;
  },

  // Get live ONT/Admin availability for chat
  getAvailability: async (): Promise<ChatAvailability> => {
    const response = await api.get('/api/v1/chat/availability');
    return response.data;
  },

  // Update thread status
  updateThreadStatus: async (threadId: string, status: string): Promise<ChatThread> => {
    const response = await api.put(`/api/v1/chat/threads/${threadId}/status`, { status });
    return response.data;
  },

  // Send a message
  sendMessage: async (threadId: string, content: string): Promise<ChatMessage> => {
    const response = await api.post(`/api/v1/chat/threads/${threadId}/messages`, { content });
    return response.data.data || response.data;
  },

  // Get messages for a thread
  getMessages: async (threadId: string, page: number = 1, limit: number = 50): Promise<ChatMessage[]> => {
    const response = await api.get(`/api/v1/chat/threads/${threadId}/messages`, {
      params: { page, page_size: limit },
    });
    return response.data.messages || response.data;
  },

  // Upload attachment
  uploadAttachment: async (messageId: string, file: File): Promise<ChatAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(`/api/v1/chat/messages/${messageId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data || response.data;
  },

  // Get attachments for a message
  getAttachments: async (messageId: string): Promise<ChatAttachment[]> => {
    const response = await api.get(`/api/v1/chat/messages/${messageId}/attachments`);
    return response.data.attachments || response.data;
  },

  // Download attachment
  downloadAttachment: async (messageId: string, attachmentId: string): Promise<Blob> => {
    const response = await api.get(`/api/v1/chat/messages/${messageId}/attachments/${attachmentId}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};
